import { ParetoChartValues } from "../dashboard/components/ParetoChart";
import { CardIntervalOptionsEnum } from "../dashboard/types";
import { GenerationType } from "../operations/types";

export type PVSyst = {
  latitude: number | null;
  longitude: number | null;
  moduleModel: string | null;
  moduleManufactor: string | null;
  stringNumber: number | null;
  stringLength: number | null;
  moduleNumber: number | null;
  moduleUnitNomPower: number | null;
  inverterManufactor: string | null;
  inverterModel: string | null;
  inverterNumber: number | null;
  inverterTotalPower: number | null;
  inverterUnitNomPower: number | null;
  nominal_STC: number | null;
  module_area: number | null;
  globHor: MonthsAndYearValues | null;
  globInc: MonthsAndYearValues | null;
  e_Grid: MonthsAndYearValues | null;
  pr: MonthsAndYearValues | null;
};
export type MonthlyValues = {
  jan?: number | null;
  feb?: number | null;
  mar?: number | null;
  apr?: number | null;
  may?: number | null;
  jun?: number | null;
  jul?: number | null;
  aug?: number | null;
  sep?: number | null;
  oct?: number | null;
  nov?: number | null;
  dec?: number | null;
};
export type MonthsAndYearValues = MonthlyValues & { year?: number };
export type Producer = {
  contactPhone?: string;
  contactEmail?: string;
  portalAccessLink?: string;
  portalAccessUsername?: string;
  portalAccessPassword?: string;
};

export type Inverters = {
  amount?: number;
  inverterPower?: number;
  producers?: Producer[];
  commands?: { name: string }[];
  firstInverterNumber?: number;
};
export type Trackers = {
  amount?: number;
  producers?: Producer[];
};
export type Modules = {
  amount?: number;
  power?: number;
  producers?: Producer[];
};
export type PlantString = {
  amount?: number;
  power?: number;
};
export type UGDiagramData = {
  disjuntorTag?: string | null;
  hasTransformer?: boolean | null;
  hasSmartLogger?: boolean | null;
};
export type SubstationUsina = {
  disjuntorTag?: string | null;
  hasTransformer?: boolean | null;
  hasSubstation?: boolean | null;
  hasDisjuntor?: boolean | null;
};
export type Components = {
  inverters?: Inverters;
  inverterDetails?: Record<string, InverterDetails>;
  trackers?: Trackers;
  modules?: Modules;
  string?: PlantString;
};
export type InverterDetails = {
  unavailableStrings?: number[];
};
export type InternetProvider = {
  contactPhone?: string;
  contactEmail?: string;
  ip?: string | null | undefined;
};
export type Concessionaire = {
  contactPhone?: string;
  contactEmail?: string;
};
export type Support = {
  internetProviders?: InternetProvider[];
  concessionaire?: Concessionaire;
  localDataServerIP?: string | null | undefined;
  idLocalizacao?: string | null | undefined;
  phoneNumber?: string | null | undefined;
};
export type Indicators = {
  totalPower?: number | null;
  modulesArea?: number | null;
  kwPeak?: number | null;
  variance?: number | null;
  efficiency?: number | null;
  predictedEnergy?: MonthlyValues | null;
  irradiance?: MonthlyValues | null;
  globInc?: MonthlyValues | null;
  pr?: MonthlyValues | null;
  megaWattValue?: number | null;
  pvSystExpectedOpticalLoss?: MonthlyValues | null;
  pvSystExpectedConversionLoss?: MonthlyValues | null;
  pvSystExpectedInverterLoss?: MonthlyValues | null;
  pvSystExpectedTransformerLoss?: MonthlyValues | null;
  pvSystExpectedSystem?: MonthlyValues | null;
  pvSystExpectedContractual?: MonthlyValues | null;
  pvSystExpectedInverter?: MonthlyValues | null;
  opticalLossesPercentage?: number | null;
  conversionLossesPercentage?: number | null;
  inverterLossesPercentage?: number | null;
  transformerLossesPercentage?: number | null;
};
export type Location = {
  latitude?: number;
  longitude?: number;
};
export type UG = {
  name?: string;
  hasTransformer?: boolean;
  hasSmartLogger?: boolean;
  disjuntorTag?: string;
  components: Components;
  djbtdjmtCommands?: { name: string }[];
};
export type UsinaIds = {
  id: string;
  ropInputId: number;
  idSolicitante?: string | null;
  idLocalizacao?: string | null;
};
export type FonteDadosUsina = {
  value: string;
};
export type PlcGatewayDevice = {
  deviceId: string;
};
export type Usina = {
  usinaIds: UsinaIds;
  name?: string;
  code?: string;
  components?: Components;
  support?: Support;
  indicators?: Indicators;
  location?: Location;
  uGs?: UG[];
  substation?: Substation;
  clusterName?: string;
  clusterId?: string;
  idSolicitante?: string;
  reportOptions?: ReportOptions;
  isRemoved?: boolean;
  fonteDadosUsina?: FonteDadosUsina[];
  generationType?: { value?: GenerationType };
  plcGatewayDevice?: PlcGatewayDevice | null;
};

export type Substation = {
  hasSubstation?: boolean | null;
  hasTransformer?: boolean | null;
  hasDisjuntor?: boolean | null;
  disjuntorTag?: string | null;
  commands?: { name: string }[];
};

export type ReportOptions = {
  disponibilidadeOnOffEnabled?: boolean;
  dailyReportEnabled?: boolean;
  monthlyReportEnabled?: boolean;
  partialReportEnabled?: boolean;
};

export type UsinaByClientCluster = {
  clusterId: string;
  clusterName: string;
};
export type UsinaByClient = {
  usinaIds: UsinaIds;
  name: string;

  clusterId?: string | null;
  cluster?: UsinaByClientCluster | null;
};
export type PostUG = {
  number?: number;
  hasTransformer?: boolean;
  hasSmartLogger?: boolean;
  disjuntorTag?: string;
  components: Components;
};

export type PostSubstation = {
  hasSubstation?: boolean | null;
  hasTransformer?: boolean | null;
  hasDisjuntor?: boolean | null;
  disjuntorTag?: string | null;
};

export type PostUsina = Omit<
  Usina,
  "usinaIds" | "uGs" | "reportOptions" | "generationType"
> & {
  idSolicitante?: string | number;
  uGs?: PostUG[];
  substation?: PostSubstation;
  reportOptions?: ReportOptions;
  newClusterName?: string;
  generationType?: GenerationType;
};

export type PutUsina = Usina;
export type PatchUsina = Partial<PostUsina>;

export type Hours =
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "18";

export type GeneratorUnitList = {
  ug: number;
  energiaGeradaMWhDia: number;
  energiaGeradaMediaPorHora: Record<string, number>;
  name: string;
};

export type EnergyGeneratedList = {
  ug: number;
  inversor: number;
  energiaGeradaMWhPorHora: Record<string, number>;
  name: string;
};

export type InverterPerformance = {
  ug: string;
  inversor: string;
  measurement: number;
  desempenho: number;
  name: string;
};

export type ParetoDaily = {
  alarms: ParetoChartValues[];
  totalAmount: number;
};

export type EnergiaGeradaInversoresMediaHoraria = {
  horas: number[];
  listaPerformanceUnidadeGeradora: GeneratorUnitList[];
  listaEnergiaGeradaInversor: EnergyGeneratedList[];
  irradiacaoRealizadaDia?: IrradiacaoRealizadaDia;
};

export type MTTRMTBFValues = {
  tempoMedioReparo: string;
  tempoMedioEntreFalhas: string;
  duracaoTotal: string;
  tempoTotalFuncionamento: string;
  quantidadeDeFalhas: number;
};

export type plantaMTTRMTBFData = {
  lastCollectTime: string;
  name: string;
  values: MTTRMTBFValues;
  uGs: {
    name: string;
    inversores: {
      name: string;
      usinaId: number;
      values: MTTRMTBFValues;
    }[];
    usinaId: number;
    values: MTTRMTBFValues;
  }[];
};

export type MatrizDeOcorrenciasByUg = Record<string, MatrizDeOcorrencias>;

export type MatrizDeOcorrencias = {
  alarms: AlarmOcorrenciasMes[];
  totalGeral: number | null;
};

export type AlarmOcorrenciasMes = {
  inversores: InversorsOccurrencesMes[];
  code: string;
  totalAmount: number | null;
  message: string;
  totalDuration: string | null;
};

export type InversorsOccurrencesMes = {
  area: string;
  amount: number | null;
  duration: string | null;
};
export type AcompanhamentoMensalData = {
  disponibilidade?: number;
  performanceRatio?: number;
  energiaGerada?: number;
};

export type AcompanhamentoMensal = {
  [key: string]: AcompanhamentoMensalData;
};

export type IrradiacaoRealizadaDia = Record<string, number>;

export type RelatorioDiarioData = {
  dataDeReferencia: string;
  dataDeGeracao: string;
  dadosUsina: {
    id: string;
    nome: string;
    cod: string;
    latitude: number;
    longitude: number;
    numeroInversores: number;
    numeroModulos: number;
    potencia: number;
  } | null;
  prIrradiacaoPrevistoRealizado: {
    prRealizado: number;
    irradiacaoRealizada: number;
  } | null;
  energiaGeradaInversoresMediaHoraria: EnergiaGeradaInversoresMediaHoraria | null;
  disponibilidadeDiaria?: Disponibilidade | null;
  performanceInversores: InverterPerformance[] | null;
  paretoDeOcorrenciasNoDia: ParetoDaily | null;
  potenciaAtivaMedia: NumberWithDateTime | null;
  disponibilidadeTotal: NumberWithDateTime | null;
  plantaMTTRMTBF: plantaMTTRMTBFData | null;
  compiladoUsina: {
    disponibilidadeOnOff: number;
    disponibilidadeOnOffEnabled: boolean;
    valorEsperado: number;
    valorGerado: number;
    energiaGerada: number;
    energiaPrevista: number;
    energiaIdeal: number;
    prReal: number;
    prPrevisto: number;
    perdas: Perdas;
  } | null;
  ocorrenciaInversoresNoMes: MatrizDeOcorrenciasByUg | null;
  acompanhamentoMensal: AcompanhamentoMensal | null;
  irradiacaoRealizadaDia: IrradiacaoRealizadaDia | null;
  trackerParetroDeOcorrenciasNoDia: ParetoDaily;
};

export type DadosUsina = {
  id: string;
  nome: string;
  cod: string;
  latitude: number;
  longitude: number;
  numeroInversores: number;
  numeroModulos: number;
  potenciaMes: number;
};

export type NumberWithDateTime = {
  dateTime: Date;
  value: number;
  usinaId: number;
  period: {
    start: Date;
    end: Date;
    startDateOnly: string;
    endDateOnly: string;
  };
  collectedAt: Date;
};

export type PrIrradiacaoPrevistoRealizadoByDate = Record<
  string,
  PrIrradiacaoPrevistoRealizado
>;

export type PrIrradiacaoPrevistoRealizado = {
  prPrevisto: number;
  prRealizado: number;
  irradiacaoPrevista: number;
  irradiacaoRealizada: number;
};

export type EnergiaGeradaInversoresMes = {
  month: number;
  year: number;
  dias: number[];
  listaPerformanceUnidadeGeradora: ListaPerformanceUnidadeGeradora[];
  listaEnergiaGeradaInversor: ListaEnergiaGeradaInversor[];
  irradiacaoRealizadaDia: IrradiacaoRealizadaDia;
};

export type ListaPerformanceUnidadeGeradora = {
  ug: number;
  energiaGeradaMWhDia: number;
  name: string;
};

export type ListaEnergiaGeradaInversor = {
  ug: number;
  inversor: number;
  energiaGeradaMWhDia: EnergiaGeradaMwhDia;
  name: string;
};

export type EnergiaGeradaMwhDia = Record<string, number>;

export type ParetoDeOcorrenciasNoMes = {
  type: string;
  title: string;
  alarms: ParetoChartValues[];
  totalAmount: number;
};

export type CompiladoUsinaMes = {
  valorEsperado: number;
  valorGerado: number;
  energiaGerada: number;
  energiaPrevista: number;
  energiaIdeal: number;
  prReal: number;
  prPrevisto: number;
  perdasMes: Perdas;
};

export type Perdas = {
  perdaDosInversoresMWh: number;
  perdaDoTrafoMWh: number;
  perdaPorIndisponibilidadeMWh: number;
  perdaNoCircuitoMWh: number;
  outrasPerdasMWh: number;
};

export type Disponibilidade = {
  disponibilidadeMedia: number;
  disponibilidadeUGs: {
    name: string;
    disponibilidade: number;
    disponibilidadeInversores: {
      name: string;
      disponibilidade: number;
    }[];
  }[];
};

export type RelatorioMensalData = {
  titulo: string;
  mesDeReferencia: number;
  anoDeReferencia: number;
  dataDeGeracao: Date;
  dadosUsina: DadosUsina;
  prIrradiacaoPrevistoRealizadoByDate: PrIrradiacaoPrevistoRealizadoByDate;
  energiaGeradaInversoresMes: EnergiaGeradaInversoresMes;
  paretoDeOcorrenciasNoMes: ParetoDeOcorrenciasNoMes;
  paretoDeOcorrenciasNoMesByArea: ParetoDeOcorrenciasNoMes;
  paretoDeOcorrenciaTrackersNoMes: ParetoDeOcorrenciasNoMes;
  compiladoUsinaMes: CompiladoUsinaMes;
  plantaMTTRMTBF: plantaMTTRMTBFData | null;
  performanceInversores: InverterPerformance[];
  disponibilidadeMedia: number;
  disponibilidadeMediaInversores: Disponibilidade;
  irradiacaoMediaRealizada: number;
  potenciaAtivaMensal: number;
  paretoDeOcorrenciaComunicacaoNoMes: ParetoDeOcorrenciasNoMes;
};

export type PerformanceUnidadeGeradora = {
  ug: number;
  energiaGeradaMWh: number;
  name: string;
};

export type EnergiaGeradaInversor = {
  ug: number;
  inversor: number;
  energiaGeradaMWhPorFaixa: Record<string, number>;
  name: string;
};

export type EnergiaGeradaInversoresBaseChartData = {
  listaPerformanceUnidadeGeradora: PerformanceUnidadeGeradora[];
  listaEnergiaGeradaInversor: EnergiaGeradaInversor[];
};

export type EnergiaGeradaInversoresHoraChartData =
  EnergiaGeradaInversoresBaseChartData & {
    tipoFaixa: "hora";
    faixa: number[];
  };

export type EnergiaGeradaInversoresDiaChartData =
  EnergiaGeradaInversoresBaseChartData & {
    tipoFaixa: "dia";
    faixa: number[];
    mes: number;
    ano: number;
  };

export type RelatorioContent = {
  interval: CardIntervalOptionsEnum;
  usinaName: string;
  pdfUrl: string;
  date: string | Date;
  hasReportToDate: boolean | undefined;
  usinaHasPermission: boolean | undefined | null;
  usinaId: string;
  relatorioHistory: RelatorioItem[];
};

export type RelatorioItem = {
  createdAt: Date;
  errors: GeracaoRelatorioError[];
  status: GeracaoRelatorioStatusEnum;
  usinaName: string;
};

export enum GeracaoRelatorioStatusEnum {
  Success,
  Error,
}

export type GeracaoRelatorioError = {
  usinaId: string;
  usinaRopInputId: number;
  interval: CardIntervalOptionsEnum;
  referenceDate: Date;
  date: string;
  message: string;
  stackTrace: string | null;
  exceptionMessage: string | null;
};

export type RelatorioPersonalizadoData = {
  dataDeReferenciaInicial: string;
  dataDeReferenciaFinal: string;
  dataDeGeracao: string;
  prIrradiacaoPrevistoRealizado: {
    prRealizado: number;
    irradiacaoRealizada: number;
  } | null;
  dadosUsina: {
    id: string;
    nome: string;
    cod: string;
    latitude: number;
    longitude: number;
    numeroInversores: number;
    numeroModulos: number;
    potencia: number;
  } | null;
  compiladoUsina: {
    disponibilidadeOnOff: number;
    disponibilidadeOnOffEnabled: boolean;
    valorEsperado: number;
    valorGerado: number;
    energiaGerada: number;
    energiaPrevista: number;
    energiaIdeal: number;
    prReal: number;
    prPrevisto: number;
    perdas: Perdas;
  } | null;
  disponibilidadePersonalizado?: Disponibilidade | null;
  potenciaAtivaMedia: NumberWithDateTime | null;
  disponibilidadeTotal: NumberWithDateTime | null;
};

export type RelatorioParcialData = {
  clientName: string;
  clientId: string;
  dataDeReferencia: string;
  dataDeGeracao: string;
  dadosUsinas: DadosUsinaParcial;
  paretoPerformanceRatio: RelatorioParcialPareto;
  paretoDisponibilidade: RelatorioParcialPareto;
  rankingUsinas: RankingUsina[];
  falhasCriticasAtivas: FalhasCriticasAtivas[];
  alarmDescriptionByCode: Record<string, string>;
};

export type DadosUsinaParcial = {
  numeroInversores: number;
  numeroModulos: number;
  potenciaNominal: number;
  potenciaMedia: number;
  disponibilidade: number;
  disponibilidadeOnOff: number;
  energiaGerada: number;
  prRealizado: number;
  irradianciaRealizada: number;
  energiaAcumulada: number;
};

export type RelatorioParcialPareto = {
  totalAmount: number;
  items: RelatorioParcialParetoItem[];
};

export type RelatorioParcialParetoItem = {
  value: number;
  usinaCode: string;
  percentage: number;
};

export type RankingUsina = {
  energiaGerada: number;
  usinaCode: string;
};

export type FalhasCriticasAtivas = {
  usinaCode: string;
  falhasPorAlarmeCode?: AlarmCodeFalhas[];
};

export type AlarmCodeFalhas = {
  alarmCode: string;
  quantidadeDeFalhas: number;
};

export type RelatorioParcialPdfFile = {
  name: string;
  mimeType: string;
  buf: Uint8Array;
};
